import React, { useEffect, useRef } from 'react';
import { PADDING_SIZE } from './NotificationsList';
import { IAlertNotification } from '../../../types/model';
import {
  formatCurrency,
  formatDate,
  formatTime,
  getLogo,
  isSameDay,
  truncateString,
} from '../../../helpers/functions';
import { Currency } from '../../../types/common';
import { useTranslation } from 'react-i18next';
import { ColorButton } from '../../UI';
import { useNavigate } from 'react-router-dom';

export interface NotificationProps {
  notification: IAlertNotification;
  style?: any;
  setSize?: any;
  index?: number;
}

const Notification: React.FC<NotificationProps> = ({
  notification,
  style,
  setSize,
  index,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { alertName, createdAt } = notification;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const item: any = {
    icon: (
      <img
        loading="lazy"
        src={getLogo(notification?.assetType, notification?.assetLogo)}
        alt="strategyAsset icon"
        className="object-cover rounded-full w-5 h-5"
      />
    ),
    iconTitle: notification.assetName,
  };

  switch (notification.notificationType) {
    case 'TARGET_PRICE_HIT':
      item.title = t('alerts.notification.targetPriceHitTitle', {
        name: notification.alertName,
      });
      item.subtitle = t('alerts.notification.targetPriceHitSubTitle');
      item.value = (
        <span>
          {formatCurrency(
            notification.alertValue,
            notification.alertCurrency as Currency['code']
          )}
        </span>
      );
      item.link = `/market/${notification.assetId}/${notification.assetType}/withoutFilter/overview`;
      break;

    case 'STOP_PROFIT_HIT':
      item.title = t('alerts.notification.stopProfitHitTitle');
      item.subtitle = t('alerts.notification.stopProfitHitSubTitle');
      item.value = (
        <span className={'text-positive'}>
          {formatCurrency(
            notification.stopProfit,
            notification.alertCurrency as Currency['code']
          )}
        </span>
      );
      item.link = `/strategies/${notification.strategyId}`;
      break;

    case 'STOP_LOSS_HIT':
      item.title = t('alerts.notification.stopProfitHitTitle');
      item.subtitle = t('alerts.notification.stopProfitHitSubTitle');
      item.value = (
        <span className={'text-negative'}>
          {formatCurrency(
            notification.stopLoss,
            notification.alertCurrency as Currency['code']
          )}
        </span>
      );
      item.link = `/strategies/${notification.strategyId}`;
      break;

    case 'STRATEGY_END':
      item.title = t('alerts.notification.strategyEndTitle');
      item.subtitle = t('alerts.notification.strategyEndSubTitle');
      item.iconTitle = notification.strategyName;
      item.icon = (
        <ColorButton
          isReadOnly={true}
          color={notification.strategyColor}
          radius={3}
        />
      );
      item.link = `/strategies/${notification.strategyId}`;
      break;

    case 'NEW_FEATURE':
      item.title = t('alerts.notification.newFeature');
      break;
  }

  useEffect(() => {
    setSize(index, ref.current?.getBoundingClientRect().height);
  }, [index, setSize]);

  return (
    <div
      className={'px-4'}
      style={{ ...style, top: `${parseFloat(style.top) + PADDING_SIZE}px` }}
    >
      <div
        className="w-full group flex justify-between p-3 rounded-xl cursor-pointer hover:bg-primary dark:hover:text-black"
        ref={ref}
        onClick={() => {
          item.link && navigate(item.link);
        }}
      >
        <div className="flex gap-3">
          <div>
            <div className={'font-medium mb-1'}>
              {truncateString(item.title, 30)}
            </div>
            <div className={'flex gap-2 mb-1 items-center'}>
              <div>{item.icon}</div>
              <div className={'text-xs'}>
                {truncateString(item.iconTitle, 30)}
              </div>
            </div>

            <div className={'text-sm text-grey'}>
              {item.subtitle}
              {item.value}
            </div>
          </div>
        </div>
        {/* Time */}
        <div className={'text-grey-50 text-sm'}>
          {isSameDay(createdAt, new Date())
            ? formatTime(createdAt, true)
            : formatDate(createdAt, true)}
        </div>
      </div>
    </div>
  );
};

export default Notification;
